<!-- ============================================================
     ELIGIBILITY SETUP PAGE
     ============================================================ -->
<div class="dashboard-card es-page">

  <!-- Save Success Toast -->
  @if (saveSuccess()) {
    <div class="success-toast-es alert alert-success d-flex align-items-center gap-3 shadow-lg" role="alert">
      <i class="bi bi-check-circle-fill fs-5"></i>
      <div>
        <strong>Eligibility rule saved successfully!</strong>
        <span class="ms-2 small">Redirecting to list…</span>
      </div>
    </div>
  }

  <!-- Page Header -->
  <div class="page-header mb-4">
    <div>
      <h1 class="page-title">
        <i class="bi bi-sliders2-vertical me-2 text-primary" style="font-size: 1.2rem;"></i>
        Eligibility Setup
      </h1>
      <p class="page-subtitle">
        Define admission eligibility rules per class, stream, and academic year
      </p>
    </div>
    @if (activeTab() === 'list') {
      <div class="d-flex gap-2">
        <a routerLink="/dashboard/{{dataService.myEncrypt('clg')}}" class="btn btn-outline-primary btn-sm d-flex align-items-center gap-2">
          <i class="bi bi-search"></i> Eligibility Check
        </a>
        <button class="btn btn-primary-erp btn-sm" (click)="openNew()">
          <i class="bi bi-plus-circle me-1"></i> New Rule
        </button>
      </div>
    } @else {
      <button class="btn btn-outline-secondary btn-sm" (click)="cancelForm()">
        <i class="bi bi-arrow-left me-1"></i> Back to List
      </button>
    }
  </div>

  <!-- ===================== LIST VIEW ===================== -->
  @if (activeTab() === 'list') {

    <!-- Filters -->
    <div class="dash-card mb-4">
      <div class="dash-card-body">
        <div class="row g-3 align-items-end">
          <div class="col-md-5">
            <label class="form-label small fw-semibold text-muted">Search Rules</label>
            <div class="input-group">
              <span class="input-group-text bg-light border-end-0"><i class="bi bi-search text-muted"></i></span>
              <input
                type="text"
                class="form-control border-start-0"
                placeholder="Rule name, class or year…"
                [(ngModel)]="searchQuery"
              >
            </div>
          </div>
          <div class="col-md-3">
            <label class="form-label small fw-semibold text-muted">Status</label>
            <select class="form-select" [(ngModel)]="filterStatus">
              <option value="">All Statuses</option>
              <option value="Active">Active</option>
              <option value="Draft">Draft</option>
              <option value="Archived">Archived</option>
            </select>
          </div>
          <div class="col-md-4">
            <label class="form-label small fw-semibold text-muted">Class / Program</label>
            <select class="form-select" [(ngModel)]="filterClass">
              <option value="">All Classes</option>
              @for (cls of classOptions; track cls) {
                <option [value]="cls.code">{{ cls.name }}</option>
              }
            </select>
          </div>
        </div>
      </div>
    </div>

    <!-- Summary Stats -->
    <div class="row g-3 mb-4">
      <div class="col-6 col-md-3">
        <div class="es-mini-stat">
          <div class="ems-icon bg-primary-gradient"><i class="bi bi-list-check"></i></div>
          <div class="ems-val">{{ rules()!.length }}</div>
          <div class="ems-label">Total Rules</div>
        </div>
      </div>
      <div class="col-6 col-md-3">
        <div class="es-mini-stat">
          <div class="ems-icon bg-success-gradient"><i class="bi bi-toggle-on"></i></div>
          <div class="ems-val text-success">{{ activeRulesCount() }}</div>
          <div class="ems-label">Active</div>
        </div>
      </div>
      <div class="col-6 col-md-3">
        <div class="es-mini-stat">
          <div class="ems-icon bg-warning-gradient"><i class="bi bi-pencil-square"></i></div>
          <div class="ems-val text-warning">{{ draftRulesCount() }}</div>
          <div class="ems-label">Draft</div>
        </div>
      </div>
        <div class="col-6 col-md-3">
          <div class="es-mini-stat">
            <div class="ems-icon bg-info-gradient"><i class="bi bi-people-fill"></i></div>
            <div class="ems-val text-info">{{ grandTotalSeats }}</div>
            <div class="ems-label">Total Seats</div>
          </div>
        </div>
    </div>

    <!-- Rules Cards Grid -->
    @if (filteredRules!.length === 0) {
      <div class="empty-state">
        <i class="bi bi-clipboard-x"></i>
        <p>No eligibility rules found.</p>
        <button class="btn btn-primary-erp" (click)="openNew()">
          <i class="bi bi-plus-circle me-1"></i> Create First Rule
        </button>
      </div>
    } @else {
      <div class="row g-3">
        @for (rule of filteredRules!; track rule.id) {
          <div class="col-lg-6 col-xl-4">
            <div class="rule-card">
              <!-- Card Header -->
              <div class="rule-card-header">
                <div class="rc-icon">
                  <i class="bi bi-journal-check"></i>
                </div>
                <div class="rc-title-wrap">
                  <h6 class="rc-title" [title]="rule.ruleName">{{ rule.ruleName }}</h6>
                  <span class="rc-year">{{ rule.academicYear }}</span>
                </div>
                <span class="rule-status-badge {{ getStatusClass(rule.status) }}">{{ rule.status }}</span>
              </div>

              <!-- Card Body -->
              <div class="rule-card-body">
                <div class="rule-info-grid">
                  <div class="ri-item">
                    <i class="bi bi-book-fill text-primary"></i>
                    <span>Class {{ dataService.getClassName(rule.programClass) }}</span>
                  </div>
                  <div class="ri-item">
                    <i class="bi bi-layers text-info"></i>
                    <span>{{ dataService.getStreamName(rule.stream) }} Stream</span>
                  </div>
                  <div class="ri-item">
                    <i class="bi bi-percent text-success"></i>
                    <span>Min {{ rule.minPercentage ?? '—' }}%</span>
                  </div>
                  <div class="ri-item">
                    <i class="bi bi-calendar-range text-warning"></i>
                    <span>Age {{ rule.minAge ?? '—' }}–{{ rule.maxAge ?? '—' }} yrs</span>
                  </div>
                  <div class="ri-item">
                    <i class="bi bi-people text-purple"></i>
                    <span>{{ getTotalSeatsForRule(rule) }} seats</span>
                  </div>
                  <div class="ri-item">
                    <i class="bi bi-file-earmark-check text-teal"></i>
                    <span>{{ (rule!.documents && rule!.documents!.filter(d => d.mandatory)!.length) || 0 }} docs required</span>
                  </div>
                </div>

                <!-- Subject pills -->
                @if (rule && rule!.subjectRequirements && rule!.subjectRequirements!.length > 0 && rule!.subjectRequirements[0]?.name) {
                  <div class="subject-pills mt-2">
                    @for (sub of rule?.subjectRequirements?.slice(0, 3); track sub.name) {
                      <span class="subject-pill" [class.mandatory-pill]="sub.mandatory">
                        {{ sub.name }}
                        @if (sub.minMarks) { <span class="ms-1 opacity-75">≥{{ sub.minMarks }}</span> }
                      </span>
                    }
                    @if (rule && rule!.subjectRequirements && rule!.subjectRequirements!.length > 3) {
                      <span class="subject-pill more-pill">+{{ rule!.subjectRequirements!.length - 3 }} more</span>
                    }
                  </div>
                }

                <!-- Entrance badge -->
                @if (rule.entranceRequired) {
                  <div class="entrance-badge mt-2">
                    <i class="bi bi-pencil-fill me-1"></i>
                    Entrance: {{ rule.entranceExamName }} (min {{ rule.minEntranceScore }} marks)
                  </div>
                }
              </div>

              <!-- Card Actions -->
              <div class="rule-card-footer">
                <button class="rc-btn rc-btn-toggle"
                  [class.active]="rule.status === 'Active'"
                  (click)="toggleStatus(rule)"
                  [title]="rule.status === 'Active' ? 'Deactivate' : 'Activate'"
                >
                  <i class="bi" [class.bi-toggle-on]="rule.status === 'Active'" [class.bi-toggle-off]="rule.status !== 'Active'"></i>
                  {{ rule.status === 'Active' ? 'Active' : 'Activate' }}
                </button>
                <div class="rc-actions">
                  <button class="rc-icon-btn" title="Edit" (click)="editRule(rule)">
                    <i class="bi bi-pencil"></i>
                  </button>
                  <button class="rc-icon-btn" title="Duplicate" (click)="duplicateRule(rule)">
                    <i class="bi bi-copy"></i>
                  </button>
                  <button class="rc-icon-btn danger" title="Delete" (click)="deleteRule(rule.id)">
                    <i class="bi bi-trash3"></i>
                  </button>
                </div>
              </div>

            </div>
          </div>
        }
      </div>
    }

  }

  <!-- ===================== FORM VIEW ===================== -->
  @if (activeTab() === 'form') {

    <div class="row g-4">
      <div class="col-12">

        <!-- Basic Info -->
        <div class="es-section-card">
          <div class="es-section-header">
            <i class="bi bi-info-circle-fill"></i> Rule Information
          </div>
          <div class="es-section-body">
            <div class="row g-3">
              <div class="col-md-6 col-lg-4">
                <label class="form-label">Rule Name <span class="required-star">*</span></label>
                <input
                  type="text"
                  class="form-control"
                  [(ngModel)]="form.ruleName"
                  placeholder="e.g. Class XI Science Admission 2025-26"
                  maxlength="120"
                  [class.is-invalid]="submitted() && !form.ruleName"
                >
                @if (submitted() && !form.ruleName) {
                  <div class="invalid-feedback">Rule name is required.</div>
                }
              </div>
              <div class="col-md-6 col-lg-4">
                <label class="form-label">Class / Program <span class="required-star">*</span></label>
                <select
                  class="form-select"
                  [(ngModel)]="form.programClass"
                  [class.is-invalid]="submitted() && !form.programClass"
                >
                  <option value="">-- Select Class / Program --</option>
                  @for (cls of classOptions; track cls) {
                    <option [value]="cls.code">{{ cls.name }}</option>
                  }
                </select>
                @if (submitted() && !form.programClass) {
                  <div class="invalid-feedback">Please select a class.</div>
                }
              </div>
              <div class="col-md-6 col-lg-2">
                <label class="form-label">Stream</label>
                <select class="form-select" [(ngModel)]="form.stream">
                  @for (s of streamOptions; track s) {
                    <option [value]="s.code">{{ s.name }}</option>
                  }
                </select>
              </div>
              <div class="col-md-6 col-lg-2">
                <label class="form-label">Academic Year <span class="required-star">*</span></label>
                <select class="form-select" [(ngModel)]="form.academicYear">
                  @for (y of academicYears; track y) {
                    <option [value]="y.name">{{ y.name }}</option>
                  }
                </select>
              </div>
              <div class="col-md-4 col-lg-2">
                <label class="form-label">Status</label>
                <select class="form-select" [(ngModel)]="form.status">
                  @for (sts of elig_status; track sts) {
                    <option [value]="sts.name">{{ sts.name }}</option>
                  }
                </select>
              </div>
              <div class="col-md-8">
                <label class="form-label">Remarks / Notes</label>
                <input type="text" class="form-control" [(ngModel)]="form.remarks" maxlength="250"
                  placeholder="Any additional notes or special conditions…">
              </div>
            </div>
          </div>
        </div>

        <!-- Age Criteria -->
        <div class="es-section-card">
          <div class="es-section-header">
            <i class="bi bi-calendar-person-fill"></i> Age Criteria
          </div>
          <div class="es-section-body">
            <div class="row g-3">
              <div class="col-md-4 col-lg-3">
                <label class="form-label">Minimum Age (years)</label>
                <input type="number" class="form-control" [(ngModel)]="form.minAge" min="3" max="60" placeholder="e.g. 15">
              </div>
              <div class="col-md-4 col-lg-3">
                <label class="form-label">Maximum Age (years)</label>
                <input type="number" class="form-control" [(ngModel)]="form.maxAge" min="3" max="60" placeholder="e.g. 18">
              </div>
              <div class="col-md-4 col-lg-3">
                <label class="form-label">Age Calculated As On</label>
                <input type="date" class="form-control" [(ngModel)]="form.ageAsOnDate">
                <div class="form-text">Age will be computed as on this date</div>
              </div>
              <div class="col-12">
                <div class="info-tip">
                  <i class="bi bi-lightbulb-fill me-2 text-warning"></i>
                  Leave blank to make age criteria optional. If both min and max are set, applicant's age on the specified date must fall within range.
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Academic Criteria -->
        <div class="es-section-card">
          <div class="es-section-header">
            <i class="bi bi-journal-bookmark-fill"></i> Academic Criteria
          </div>
          <div class="es-section-body">
            <div class="row g-3 mb-3">
              <div class="col-md-4 col-lg-3">
                <label class="form-label">Min. Percentage / CGPA <span class="required-star">*</span></label>
                <div class="input-group">
                  <input
                    type="number"
                    class="form-control"
                    [(ngModel)]="form.minPercentage"
                    min="0" max="100" step="0.5"
                    placeholder="e.g. 60"
                    [class.is-invalid]="submitted() && form.minPercentage === null"
                  >
                  <span class="input-group-text">%</span>
                </div>
                @if (submitted() && form.minPercentage === null) {
                  <div class="invalid-feedback d-block">Required.</div>
                }
              </div>
              <div class="col-md-4 col-lg-2">
                <label class="form-label">Grace Marks</label>
                <input type="number" class="form-control" [(ngModel)]="form.graceMarks" min="0" max="20" placeholder="0">
              </div>
              <div class="col-md-4 col-lg-3">
                <label class="form-label">Required Board</label>
                <select class="form-select" [(ngModel)]="form.requiredBoard">
                  @for (b of boardOptions; track b) {
                    <option [value]="b.code">{{ b.code }} - {{ b.name }}</option>
                  }
                </select>
              </div>
              <div class="col-md-4 col-lg-2">
                <label class="form-label">Passing Year From</label>
                <input type="number" class="form-control" [(ngModel)]="form.passingYearFrom" min="2000" max="2030" placeholder="e.g. 2024">
              </div>
              <div class="col-md-4 col-lg-2">
                <label class="form-label">Passing Year To</label>
                <input type="number" class="form-control" [(ngModel)]="form.passingYearTo" min="2000" max="2030" placeholder="e.g. 2025">
              </div>
            </div>

            <!-- Subject Requirements -->
            <p class="sub-label"><i class="bi bi-list-ol me-2"></i>Subject-wise Minimum Marks</p>
            @for (sub of form.subjectRequirements; track sub; let i = $index) {
              <div class="subject-req-row">
                <div class="row g-2 align-items-center">
                  <div class="col-auto text-muted small fw-semibold" style="min-width:28px;">{{ i + 1 }}.</div>
                  <div class="col-md">
                    <input type="text" class="form-control form-control-sm"
                      [(ngModel)]="sub.name" placeholder="Subject name" maxlength="60"
                      [name]="'subName' + i">
                  </div>
                  <div class="col-md-3">
                    <div class="input-group input-group-sm">
                      <span class="input-group-text">Min</span>
                      <input type="number" class="form-control" [(ngModel)]="sub.minMarks"
                        min="0" max="100" placeholder="marks" [name]="'subMarks' + i">
                    </div>
                  </div>
                  <div class="col-auto">
                    <div class="form-check mb-0">
                      <input class="form-check-input" type="checkbox"
                        [(ngModel)]="sub.mandatory" [id]="'mandatory' + i" [name]="'mandatory' + i">
                      <label class="form-check-label small" [for]="'mandatory' + i">Mandatory</label>
                    </div>
                  </div>
                  <div class="col-auto">
                    <button type="button" class="btn-remove-sm" (click)="removeSubject(i)"
                      [disabled]="form.subjectRequirements!.length === 1" title="Remove">
                      <i class="bi bi-x-lg"></i>
                    </button>
                  </div>
                </div>
              </div>
            }
            <button type="button" class="btn btn-add-row mt-2" (click)="addSubject()">
              <i class="bi bi-plus-circle me-1"></i> Add Subject
            </button>
          </div>
        </div>

        <!-- Category Seats -->
        <div class="es-section-card">
          <div class="es-section-header">
            <i class="bi bi-bar-chart-steps"></i> Category-wise Seat Allocation
            <span class="ms-auto badge bg-light text-dark small fw-semibold border">
              Total: {{ totalSeats }} seats
            </span>
          </div>
          <div class="es-section-body">
            <div class="table-responsive">
              <table class="erp-table seats-table">
                <thead>
                  <tr>
                    <th>Category</th>
                    <th>Total Seats</th>
                    <th>Reservation %</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  @for (seat of form.categorySeats; track seat; let i = $index) {
                    <tr>
                      <td>
                        <select class="form-select form-select-sm" [(ngModel)]="seat.category"
                          [name]="'catName' + i">
                          @for (c of allCategories; track c) {
                            <option [value]="c.code">{{ c.code }} - {{ c.name }}</option>
                          }
                        </select>
                      </td>
                      <td>
                        <input type="number" class="form-control form-control-sm"
                          [(ngModel)]="seat.totalSeats" min="0" placeholder="0"
                          [name]="'catSeats' + i">
                      </td>
                      <td>
                        <div class="input-group input-group-sm">
                          <input type="number" class="form-control"
                            [(ngModel)]="seat.reservedPct" min="0" max="100" placeholder="0"
                            [name]="'catPct' + i">
                          <span class="input-group-text">%</span>
                        </div>
                      </td>
                      <td>
                        <button type="button" class="btn-remove-sm" (click)="removeCategoryRow(i)"
                          [disabled]="form.categorySeats!.length === 1">
                          <i class="bi bi-trash3"></i>
                        </button>
                      </td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>
            @if (form.categorySeats && form.categorySeats.length < allCategories.length) {
              <button type="button" class="btn btn-add-row mt-2" (click)="addCategoryRow()">
                <i class="bi bi-plus-circle me-1"></i> Add Category
              </button>
            }
          </div>
        </div>

        <!-- Entrance Exam -->
        <div class="es-section-card">
          <div class="es-section-header">
            <i class="bi bi-pencil-square"></i> Entrance Exam (Optional)
          </div>
          <div class="es-section-body">
            <div class="row g-3 align-items-center">
              <div class="col-auto">
                <div class="form-check form-switch">
                  <input class="form-check-input" type="checkbox" id="entranceReq"
                    [(ngModel)]="form.entranceRequired" name="entranceReq">
                  <label class="form-check-label fw-semibold" for="entranceReq">
                    Entrance Exam Required
                  </label>
                </div>
              </div>
            </div>
            @if (form.entranceRequired) {
              <div class="row g-3 mt-1">
                <div class="col-md-6">
                  <label class="form-label">Exam Name</label>
                  <input type="text" class="form-control" [(ngModel)]="form.entranceExamName"
                    placeholder="e.g. Institute Entrance Test (IET)" maxlength="100">
                </div>
                <div class="col-md-3">
                  <label class="form-label">Minimum Score</label>
                  <input type="number" class="form-control" [(ngModel)]="form.minEntranceScore"
                    min="0" max="100" placeholder="e.g. 40">
                </div>
              </div>
            }
          </div>
        </div>

        <!-- Documents -->
        <div class="es-section-card">
          <div class="es-section-header">
            <i class="bi bi-file-earmark-check-fill"></i> Required Documents
          </div>
          <div class="es-section-body">
            <div class="row g-2">
              @for (doc of form.documents; track doc; let i = $index) {
                <div class="col-md-6 col-lg-4">
                  <div class="doc-row">
                    <input type="text" class="form-control form-control-sm flex-fill"
                      [(ngModel)]="doc.name" [name]="'docName' + i" placeholder="Document name">
                    <div class="form-check form-switch ms-2 mb-0">
                      <input class="form-check-input" type="checkbox"
                        [(ngModel)]="doc.mandatory" [id]="'docMandatory' + i" [name]="'docMandatory' + i">
                      <label class="form-check-label small" [for]="'docMandatory' + i">Req.</label>
                    </div>
                    <!-- <button type="button" class="btn-remove-sm ms-1"
                      (click)="form.documents.splice(i, 1)" title="Remove">
                      <i class="bi bi-x-lg"></i>
                    </button> -->
                  </div>
                </div>
              }
              <div class="col-12">
                <button type="button" class="btn btn-add-row mt-1"
                  (click)="form.documents.push({ name: '', mandatory: false })">
                  <i class="bi bi-plus-circle me-1"></i> Add Document
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Other Conditions -->
        <!-- <div class="es-section-card">
          <div class="es-section-header">
            <i class="bi bi-shield-check-fill"></i> Other Conditions
          </div>
          <div class="es-section-body">
            <div class="row g-3">
              <div class="col-auto">
                <div class="form-check form-switch">
                  <input class="form-check-input" type="checkbox" id="domicile"
                    [(ngModel)]="form.domicileRequired" name="domicile">
                  <label class="form-check-label" for="domicile">Domicile Certificate Required</label>
                </div>
              </div>
              <div class="col-auto">
                <div class="form-check form-switch">
                  <input class="form-check-input" type="checkbox" id="medical"
                    [(ngModel)]="form.medicalFitness" name="medical">
                  <label class="form-check-label" for="medical">Medical Fitness Certificate Required</label>
                </div>
              </div>
            </div>
          </div>
        </div> -->

        <!-- Form Actions -->
        <div class="es-form-actions">
          <button type="button" class="btn btn-outline-secondary" (click)="cancelForm()">
            <i class="bi bi-x-circle me-2"></i> Cancel
          </button>
          <button type="button" class="btn btn-primary-erp" (click)="saveForm()">
            <i class="bi bi-check-circle me-2"></i>
            {{ editingId() !== null ? 'Update Rule' : 'Save Rule' }}
          </button>
        </div>

      </div>
    </div>
  }

</div>
